import { useCallback, useContext, useEffect, useMemo } from 'react';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { ActivityLogModal } from 'src/modals/ActivityLog';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { ModalBody, ModalFooter } from 'src/modals/Modal';
import { ModalBodyDataContainer } from 'src/modals/Modal/Modal.styled';
import { SplitListingModal } from 'src/modals/SplitListing';
import { CopyIcon, SplitIcon } from 'src/svgs';
import { HistoryIcon, IconsFill } from 'src/svgs/Viagogo';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { getUrlWithDeepLink } from 'src/utils/deepLinkUtils';
import { ListingDetails } from 'src/WebApiController';

type TileActionProps = {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
};

export const useListingTileActions = () => {
  const { setModal } = useContext(ModalContext);
  const { posEntity: listing } = useActivePosEntityContext<ListingDetails>();
  const { copyToClipboard } = useCopyToClipboard();

  const onLaunchSplitListingDialog = useCallback(() => {
    if (!listing) return;

    setModal({
      children: <SplitListingModal />,
      clickOutsideToClose: true,
      size: 'fullscreen',
    });
  }, [listing, setModal]);

  const onLaunchHistoryLog = useCallback(() => {
    setModal({
      children: <ActivityLogModal />,
      clickOutsideToClose: false,
      size: 'fullscreen',
    });
  }, [setModal]);

  const onCopyListingDeeplink = useCallback(() => {
    const url = getUrlWithDeepLink(InventoryDeeplinkQueryParam);
    if (url) {
      copyToClipboard(url.toString(), 'CopyListingId');

      setModal({
        children: <ListingIdCopyModal />,
        clickOutsideToClose: false,
        size: 'sm',
      });
    }
  }, [copyToClipboard, setModal]);

  return useMemo<TileActionProps[]>(() => {
    const actions: TileActionProps[] = [];
    actions.push({
      icon: <SplitIcon fill={IconsFill.textBrand} size={vars.iconSize.l} />,
      label: <Content id={ContentId.Split} />,
      onClick: onLaunchSplitListingDialog,
    });

    actions.push({
      icon: <CopyIcon fill={IconsFill.textBrand} size={vars.iconSize.l} />,
      label: <Content id={ContentId.CopyId} />,
      onClick: onCopyListingDeeplink,
    });

    actions.push({
      icon: <HistoryIcon fill={IconsFill.textBrand} size={vars.iconSize.l} />,
      label: <Content id={ContentId.History} />,
      onClick: onLaunchHistoryLog,
    });

    return actions;
  }, [onCopyListingDeeplink, onLaunchHistoryLog, onLaunchSplitListingDialog]);
};

const ListingIdCopyModal = () => {
  const { closeModal } = useContext(ModalContext);

  useEffect(() => {
    const autoClose = setTimeout(() => {
      closeModal(true);
    }, 3000);
    return () => clearTimeout(autoClose);
  }, [closeModal]);

  return (
    <>
      <CancellableFormHeader>
        <ConnectedEventEntityHeader
          title={<Content id={ContentId.LinkCopied} />}
        />
      </CancellableFormHeader>

      <ModalBody>
        <ModalBodyDataContainer style={{ height: 'auto !important' }}>
          <Content id={ContentId.LinkHasBeenCopiedToClipboard} />
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <OkButton onClick={closeModal} textContentId={ContentId.OK} />
      </ModalFooter>
    </>
  );
};
